import { ReactNode } from "react";

import {
    AbfLoanExpanded,
    LoopPositionExpanded,
    getZcCollateral,
    isLoopLoan,
    isRaydiumMetadata,
    isWhirlpoolMetadata,
    LoopRouteType
} from "@bridgesplit/abf-react";
import { Button, DialogWrapper, PopoverWrapper, SectionedMenuItems, SPACING, Text, usePopover } from "@bridgesplit/ui";
import { ExpandMore } from "@mui/icons-material";
import { NAV_BREAKPOINT } from "app/components/navbar";
import { AppDialog, useAppDialog } from "app/utils";

import { useLoanActions } from "../side-card/CardLoanActionsButton";
import { LoanAction } from "../side-card/types";
import { hasRolloverPtMint, handleLoanAction, useLoanActionMenuItemSections } from "./util";
import RolloverPt from "./RolloverPt";
import { ManageLeverageDialog } from "./ManageLeverageDialog";
import { ManageWhirlpool } from "./whirlpool";
import { ManageRaydium } from "./raydium";
import { AdjustDebt } from "./AdjustDebt";
import { ManualManageDialog } from "./ManualManageDialog";

export type LoanPrimaryAction = {
    label: string;
    component: ReactNode;
    dialogHeaderLabel?: string;
    isDrawer?: boolean;
};

export type LoanManageLiquidityAction = {
    component: ReactNode;
    dialogHeaderLabel: string;
    isDrawer: boolean;
};

export function getLoanPrimaryAction({
    loanExpanded,
    loopPosition,
    isMobile
}: {
    loanExpanded: AbfLoanExpanded | undefined;
    loopPosition: LoopPositionExpanded | undefined;
    isMobile: boolean;
}): LoanPrimaryAction {
    const metadata = getZcCollateral(loanExpanded)?.metadata;
    if (hasRolloverPtMint(metadata)) {
        return {
            label: "Rollover",
            component: <RolloverPt loanExpanded={loanExpanded} />
        };
    }

    const manageLiqLabel = isMobile ? "Manage" : "Manage liquidity";
    if (isWhirlpoolMetadata(metadata)) {
        return {
            label: manageLiqLabel,
            component: <ManageWhirlpool loanExpanded={loanExpanded} />,
            isDrawer: true
        };
    }
    if (isRaydiumMetadata(metadata)) {
        return {
            label: manageLiqLabel,
            component: <ManageRaydium loanExpanded={loanExpanded} />,
            isDrawer: true
        };
    }

    if (isLoopLoan(loanExpanded)) {
        const isManual = loopPosition?.loopExpanded?.loopVault.routeType === LoopRouteType.Manual;
        return {
            label: "Adjust leverage",
            component: isManual ? (
                <ManualManageDialog loanExpanded={loanExpanded} loopPosition={loopPosition} />
            ) : (
                <ManageLeverageDialog loanExpanded={loanExpanded} loopPosition={loopPosition} />
            )
        };
    }

    return {
        label: "Adjust debt",
        component: <AdjustDebt loanExpanded={loanExpanded} loopPosition={loopPosition} isNestedDialogView={true} />
    };
}

export function getLoanManageLiquidityAction({
    loanExpanded
}: {
    loanExpanded: AbfLoanExpanded | undefined;
}): LoanManageLiquidityAction | undefined {
    const collateral = getZcCollateral(loanExpanded);
    if (isWhirlpoolMetadata(collateral?.metadata)) {
        return {
            component: <ManageWhirlpool loanExpanded={loanExpanded} />,
            dialogHeaderLabel: "Manage liquidity",
            isDrawer: true
        };
    }

    if (isRaydiumMetadata(collateral?.metadata)) {
        return {
            component: <ManageRaydium loanExpanded={loanExpanded} />,
            dialogHeaderLabel: "Manage liquidity",
            isDrawer: true
        };
    }
    return undefined;
}

export function LoanActionsDialog() {
    const { getData } = useAppDialog();
    const data = getData(AppDialog.LoanActions);

    return (
        <DialogWrapper maxWidth={data?.maxWidth} drawerQuery={NAV_BREAKPOINT} isDrawer={!!data?.isDrawer}>
            {data?.action?.dialogHeaderLabel && <Text variant="h4">{data?.action?.dialogHeaderLabel}</Text>}
            {data?.action?.component}
        </DialogWrapper>
    );
}

export function LoanActionButtonsMore({
    onActionClick,
    loanExpanded,
    loopPosition,
    isNestedDialogView
}: {
    onActionClick: (action: LoanAction) => void;
    loanExpanded: AbfLoanExpanded | undefined;
    loopPosition: LoopPositionExpanded | undefined;
    isNestedDialogView: boolean;
}) {
    const { open: openPopover, popoverProps, close } = usePopover();
    const collateral = getZcCollateral(loanExpanded);
    const principalMetadata = loanExpanded?.principalMetadata;
    const collateralMetadata = collateral?.metadata;

    const actionGroups = useLoanActions(
        loanExpanded,
        loopPosition,
        principalMetadata,
        collateralMetadata,
        isNestedDialogView,
        undefined
    );
    const sections = useLoanActionMenuItemSections(actionGroups, async (action) => {
        close();
        await handleLoanAction(action, onActionClick);
    });

    const hasAnyItems = sections.some((section) => section.length > 0);
    if (!hasAnyItems) return null;

    return (
        <>
            <Button variant="outlined" size="small" onClick={openPopover}>
                More <ExpandMore fontSize="small" />
            </Button>
            <PopoverWrapper
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ horizontal: "right", vertical: -SPACING }}
                {...popoverProps}
            >
                <SectionedMenuItems items={sections} close={close} />
            </PopoverWrapper>
        </>
    );
}
